'use client';

import { StepperStep } from '@/components/NewGeneralDialog/DialogStepper';

import { ComponentLoader } from '@/components/ComponentLoader';
import { SignDocumentStep } from '@/components/Documents/SignDocumentStep';
import { FlowCompletion } from '@/components/FlowCompletion';
import { NewGeneralDialog } from '@/components/NewGeneralDialog';
import { WizardAnimationWrapper } from '@/components/WizardAnimationWrapper';
import { useGeneralDialogStepper } from '@/hooks/useGeneralDialogStepper';
import { Documents } from '@/types/company';
import { Button, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { t } from 'i18next';
import { useSession } from 'next-auth/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GuaranteeDocument } from '../../company/(Documents)/GuaranteeDocument';

enum Steps {
  Review,
  Sign,
  FinalStep,
}

export const SignGuaranteeDocument = ({
  onClose,
  open,
  document,
}: {
  onClose: () => void;
  open: boolean;
  document: Documents.DocumentType;
}) => {
  const { t } = useTranslation();

  const STEPS: StepperStep[] = [
    {
      label: t('review'),
      status: 'not_completed',
    },
    {
      label: t('sign'),
      status: 'not_completed',
    },
  ];

  return (
    <NewGeneralDialog
      isOpen={open}
      onClose={onClose}
      steps={STEPS}
      initialActiveStepIndex={Steps.Review}
      slotsProps={{
        dialogContent: {
          sx: {
            padding: 2,
          },
        },
      }}>
      <DialogBody onModalClose={onClose} document={document} />
    </NewGeneralDialog>
  );
};

const DialogBody = ({
  onModalClose,
  document,
}: {
  onModalClose: () => void;
  document: Documents.DocumentType;
}) => {
  const [onNext, setOnNext] = useState(true);

  const { data, status } = useSession({ required: true });
  const queryClient = useQueryClient();

  const companyId = data?.user?.company_id;
  const country = data?.user?.company?.country;

  const { hideStepper, moveBackward, moveForward, activeStepIndex } =
    useGeneralDialogStepper();

  const isLoading = status === 'loading' || !companyId;

  const handleNextStep = () => {
    setOnNext(true);
    setTimeout(() => {
      moveForward();
    }, 100);
  };

  const handlePreviousStep = () => {
    setOnNext(false);
    setTimeout(() => {
      moveBackward();
    }, 100);
  };

  const onDone = () => {
    queryClient.invalidateQueries({
      queryKey: ['company-documents', data?.user?.company?.id],
    });
    onModalClose();
  };

  if (isLoading) return <ComponentLoader />;

  const GUARANTEE_STEPS_CONTENT = [
    <GuaranteeDocument
      handleNextStep={handleNextStep}
      onNext={onNext}
      document={document}
      key={Steps.Review}
    />,
    country && (
      <SignDocumentStep
        handleNextStep={() => {
          hideStepper();
          handleNextStep();
        }}
        handleOnBackClick={handlePreviousStep}
        country={country}
        documentId={document?.id}
        key={Steps.Sign}
      />
    ),
    <WizardAnimationWrapper lastStep key={Steps.FinalStep}>
      <FlowCompletion
        title={t('document_signed')}
        subtitle={t('document_signed_subtitle', {
          company: data?.user?.company?.name,
        })}
        actionButtons={
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            onClick={onDone}>
            {t('done')}
          </Button>
        }
      />
    </WizardAnimationWrapper>,
  ];

  return (
    <AnimatePresence mode="popLayout">
      {activeStepIndex === GUARANTEE_STEPS_CONTENT.length - 1 ? (
        GUARANTEE_STEPS_CONTENT[activeStepIndex]
      ) : (
        <Stack
          marginX="auto"
          width="100%"
          minHeight="100%"
          maxWidth={960}
          key={activeStepIndex}
          marginBottom={5}>
          {GUARANTEE_STEPS_CONTENT[activeStepIndex]}
        </Stack>
      )}
    </AnimatePresence>
  );
};
